import { ICommonSettings, IParamPaymentSettings } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigParamPaymentSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingParamPaymentSettings?: IParamPaymentSettings;
}

export interface IConfigParamPaymentSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingParamPaymentId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigParamPaymentSettings extends React.PureComponent<
  IConfigParamPaymentSettingsProps,
  IConfigParamPaymentSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigParamPaymentSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadParamPaymentSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigParamPaymentSettingsProps) {
    if (prevProps.editingParamPaymentId && !this.props.editingParamPaymentId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingParamPaymentId !== this.props.editingParamPaymentId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadParamPaymentSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadParamPaymentSetting();
    }
  }

  loadParamPaymentSetting = () => {
    if (!this.props.editingParamPaymentId) return;

    const editingParamPaymentSettings = this.props.commonSettings.paramPaymentSettings?.find(
      (x) => x.id === this.props.editingParamPaymentId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (!editingParamPaymentSettings) return;
    this.setState({ editingParamPaymentSettings }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingParamPaymentSettings);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const paramPaymentSettings = this.props.commonSettings?.paramPaymentSettings;
    if (!paramPaymentSettings) return;

    const isSettingAlreadyExist = paramPaymentSettings.some(
      (x) => x.name === value && x.id !== this.props.editingParamPaymentId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (isSettingAlreadyExist) throw new Error("Param Payment Setting Name is already exist.");
  };

  isGuidValidator = async (_rule, value) => {
    if (!value) return;
    if (!Guid.isGuid(value.trim())) throw new Error("Please enter a valid Guid");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IParamPaymentSettings;
    if (this.props.editingParamPaymentId) {
      obj = {
        id: this.props.editingParamPaymentId,
        name: data.name,
        clientCode: data.clientCode,
        clientUserName: data.clientUserName?.trim(),
        clientPassword: data.clientPassword?.trim(),
        clientGuid: data.clientGuid?.trim(),
        baseUrl: data.baseUrl?.trim()
      };
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "paramPaymentSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.name,
        clientCode: data.clientCode,
        clientUserName: data.clientUserName?.trim(),
        clientPassword: data.clientPassword?.trim(),
        clientGuid: data.clientGuid?.trim(),
        baseUrl: data.baseUrl?.trim()
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "paramPaymentSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteParamPayment = () => {
    if (!this.props.editingParamPaymentId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingParamPaymentId || "",
      configKey: "paramPaymentSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Param Payment Setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Param payment setting"
        secondHeader="Param payment setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingParamPaymentId ? this.deleteParamPayment : undefined}
            deleteTestId="paramPaymentSettingDeleteButton"
            submitTestId="paramPaymentSettingCreateButton"
            submitLabel={this.props.editingParamPaymentId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configParamPaymentForm"
          />
        }
      >
        <Form
          id="configParamPaymentForm"
          name="paramPaymentSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("appsettings_parampaymentsettings_description")}
          >
            <div>
              <ConfigField label="Name" required tooltipContentKey="appsettings_parampaymentsettings_name">
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter a Param payment settings Name." },
                    { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Param payment settings Name is invalid" },
                    { validator: this.existingNameValidator }
                  ]}
                >
                  <KuikaInput size="md" data-testid="paramPaymentSettingNameInput" autoFocus />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Client Code" required tooltipContentKey="appsettings_parampaymentsettings_clientcode">
                <Form.Item
                  name="clientCode"
                  rules={[
                    { required: true, message: "Param Payment Client Code is required" },
                    { type: "integer", message: "Param Payment Client Code should be decimal" }
                  ]}
                >
                  <KuikaInput size="md" type="number" data-testid="paramSettingClientCodeInput" />
                </Form.Item>
              </ConfigField>
              <ConfigField
                label="Client User Name"
                required
                tooltipContentKey="appsettings_parampaymentsettings_clientusername"
              >
                <Form.Item
                  rules={[{ required: true, message: "Param Payment Setting Client User Name is required" }]}
                  name="clientUserName"
                >
                  <KuikaInput size="md" data-testid="paramSettingClientUserNameInput" />
                </Form.Item>
              </ConfigField>
              <ConfigField
                label="Client Password"
                required
                tooltipContentKey="appsettings_parampaymentsettings_clientpassword"
              >
                <Form.Item
                  rules={[{ required: true, message: "Param Payment Settings Client Password is required" }]}
                  name="clientPassword"
                >
                  <KuikaInput size="md" data-testid="paramSettingClientPasswordInput" />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Client Guid" required tooltipContentKey="appsettings_parampaymentsettings_clientguid">
                <Form.Item
                  rules={[
                    { required: true, message: "Param Payment Settings Client Guid is required" },
                    { validator: this.isGuidValidator }
                  ]}
                  name="clientGuid"
                >
                  <KuikaInput size="md" data-testid="paramSettingClientGuidInput" />
                </Form.Item>
              </ConfigField>
              <ConfigField label="URL" required tooltipContentKey="appsettings_parampaymentsettings_url">
                <Form.Item
                  rules={[{ required: true, message: "Param Payment Settings URL is required." }]}
                  name="baseUrl"
                >
                  <KuikaInput size="md" data-testid="paramSettingBaseUrlInput" />
                </Form.Item>
              </ConfigField>
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
